import { colorTintBase } from './ColorTint';
/**
 * Every named color a component's `color` prop accepts. Tints of these are accepted too, written as
 * '<style>-<step>' (e.g. 'primary-20', see COLOR_TINT_STEPS)
 */
export const COLOR_STYLES = {
    DEFAULT: 'default',
    PRIMARY: 'primary',
    SECONDARY: 'secondary',
    ACTIVE: 'active',
    WARNING: 'warning',
    DANGER: 'danger',
    SUCCESS: 'success',
    INFO: 'info',
    DARK: 'dark',
    LIGHT: 'light',
    HIGHLIGHT: 'highlight',
    POSITIVE: 'positive',
    NEGATIVE: 'negative',
    NEUTRAL: 'neutral',
    GRADE_A: 'grade-a',
    GRADE_B: 'grade-b',
    GRADE_C: 'grade-c',
    GRADE_D: 'grade-d',
    GRADE_F: 'grade-f',
};
//  Theme variables (bg, fg) behind each style
const COLOR_STYLE_VARS = {
    'default': ['--c-brand-default-bg', '--c-brand-default-fg'],
    'primary': ['--c-brand-primary-bg', '--c-brand-primary-fg'],
    'secondary': ['--c-brand-secondary-bg', '--c-brand-secondary-fg'],
    'active': ['--c-brand-active-bg', '--c-brand-active-fg'],
    'warning': ['--c-brand-warning-bg', '--c-brand-warning-fg'],
    'danger': ['--c-brand-danger-bg', '--c-brand-danger-fg'],
    'success': ['--c-brand-success-bg', '--c-brand-success-fg'],
    'info': ['--c-brand-info-bg', '--c-brand-info-fg'],
    'dark': ['--c-brand-dark-bg', '--c-brand-dark-fg'],
    'light': ['--c-brand-light-bg', '--c-brand-light-fg'],
    'highlight': ['--c-brand-highlight-bg', '--c-brand-highlight-fg'],
    'positive': ['--c-sentiment-positive-bg', '--c-sentiment-positive-fg'],
    'negative': ['--c-sentiment-negative-bg', '--c-sentiment-negative-fg'],
    'neutral': ['--c-sentiment-neutral-bg', '--c-sentiment-neutral-fg'],
    'grade-a': ['--c-grade-a-bg', '--c-grade-a-fg'],
    'grade-b': ['--c-grade-b-bg', '--c-grade-b-fg'],
    'grade-c': ['--c-grade-c-bg', '--c-grade-c-fg'],
    'grade-d': ['--c-grade-d-bg', '--c-grade-d-fg'],
    'grade-f': ['--c-grade-f-bg', '--c-grade-f-fg'],
};
//  From this step up a tint keeps its style's fg color; lighter tints use the regular text color so
//  text stays readable
const TINT_FG_FROM = 60;
/**
 * Resolves a COLOR_STYLES value, a tint of one, or any CSS color to CSS values. Returns null for an
 * empty value, or one that isn't a style or a valid CSS color.
 * Tints mix the style's color into the panel background, matching color-tint() in sass/_color_styles.scss
 */
export function resolveColor(value, { aliases = {}, allowCustom = true } = {}) {
    if (typeof value !== 'string' || value.trim() === '')
        return null;
    const base = colorTintBase(value);
    const name = (aliases[base] ?? base);
    const vars = COLOR_STYLE_VARS[name];
    if (vars) {
        const [bg, fg] = vars;
        if (base === value)
            return { bg: `var(${bg})`, fg: `var(${fg})` };
        const step = Number(value.slice(base.length + 1));
        return {
            bg: `color-mix(in srgb, var(${bg}) ${step}%, var(--c-panel-bg))`,
            fg: step >= TINT_FG_FROM ? `var(${fg})` : 'var(--c-txt)',
        };
    }
    if (!allowCustom)
        return null;
    if (typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && !CSS.supports('color', value))
        return null;
    return { bg: value, fg: null };
}
/**
 * Resolves a color (see resolveColor) into CSS custom properties for a component's inline style, e.g.
 * colorVars('primary-20', {bg: '--button-bg', fg: '--button-fg'}). Returns an empty object when the
 * color doesn't resolve, so the component's defaults apply
 */
export function colorVars(value, names, options = {}) {
    const color = resolveColor(value, options);
    if (!color)
        return {};
    const vars = { [names.bg]: color.bg };
    if (names.fg && color.fg)
        vars[names.fg] = color.fg;
    return vars;
}
/**
 * Resolves a color class from the color class name and an optional tint.
 * Tints go from 10-90 in intervals of 10
 * @param colorClass
 * @param tint
 * @returns
 */
export function buildColor(colorClass, tint, { aliases = {}, allowCustom = true } = {}) {
    return resolveColor(buildColorString(colorClass, tint), { aliases, allowCustom });
}
/**
 * A helper to build a color string like `primary-10` or `primary`
 *
 * buildColorString('primary', 10)
 * buildColorString(COLOR_STYLES.primary, 10)
 * @param colorClass
 * @param tint
 * @returns
 */
export function buildColorString(colorClass, tint) {
    if (tint) {
        return `${colorClass}-${tint}`;
    }
    return colorClass;
}
//# sourceMappingURL=ColorStyle.js.map